<template>
  <gas-page-shell :breadcrumb="['物联网卡管理', '物联网卡列表']">
    <div class="stat-row">
      <div v-for="item in statCards" :key="item.key" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }"><i :class="item.icon"></i></div>
        <div class="stat-body">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value" :style="{ color: item.valueColor || '#1d2129' }">{{ item.value }}</div>
        </div>
      </div>
    </div>

    <div class="search-section">
      <div class="section-title"><span class="title-bar"></span>检索条件</div>
      <el-form :inline="true" :model="query" size="small" label-width="88px" class="search-form">
        <el-form-item label="运营商">
          <el-select v-model="query.operatorName" clearable placeholder="全部运营商" style="width:140px">
            <el-option v-for="o in operatorOptions" :key="o" :label="o" :value="o" />
          </el-select>
        </el-form-item>
        <el-form-item label="卡类型">
          <el-select v-model="query.cardType" clearable placeholder="全部卡类型" style="width:130px">
            <el-option v-for="t in cardTypeOptions" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="所属组织">
          <el-select v-model="query.orgId" clearable placeholder="全部组织" style="width:160px">
            <el-option v-for="o in orgOptions" :key="o.orgId" :label="o.orgName" :value="o.orgId" />
          </el-select>
        </el-form-item>
        <el-form-item label="所属项目">
          <el-select v-model="query.projectName" clearable filterable placeholder="全部项目" style="width:160px">
            <el-option v-for="p in projectOptions" :key="p" :label="p" :value="p" />
          </el-select>
        </el-form-item>
        <el-form-item label="采购时间">
          <el-date-picker
            v-model="purchaseRange"
            type="datetimerange"
            range-separator="-"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            style="width:360px"
            @change="onPurchaseRangeChange"
          />
        </el-form-item>
        <el-form-item>
          <el-button icon="el-icon-refresh-left" @click="resetSearch">重置</el-button>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div ref="tablePanel" class="table-panel">
      <div class="table-toolbar">
        <div class="table-title-group">
          <span class="table-title">物联网卡列表 <em>共 {{ total }} 条记录</em></span>
          <gas-table-toolbar
            :columns="tableColumnDefs"
            :visible-map.sync="tableColumnVisible"
            :selected-count="selectedIds.length"
            :show-batch-delete="tableCanBatchDelete"
            @batch-delete="batchRemove"
          />
        </div>
        <div class="toolbar-actions">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate(emptyForm())">新增</el-button>
          <el-button size="small" class="btn-export" icon="el-icon-document" @click="exportExcel">批量导出Excel</el-button>
          <el-button size="small" icon="el-icon-refresh" circle title="刷新" @click="refreshAll" />
          <el-button size="small" icon="el-icon-full-screen" circle title="全屏" @click="toggleFullscreen" />
        </div>
      </div>

      <el-table :data="list" v-loading="loading" border size="small" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="44" align="center" />
        <el-table-column label="序号" width="60" align="center">
          <template slot-scope="{ $index }">{{ rowNo($index) }}</template>
        </el-table-column>
        <el-table-column label="运营商" width="110" align="center">
          <template slot-scope="{ row }">
            <span v-if="row.operatorName" class="operator-tag" :class="operatorClass(row.operatorName)">{{ row.operatorName }}</span>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column prop="cardType" label="卡类型" width="96" align="center" />
        <el-table-column label="采购时间" min-width="158">
          <template slot-scope="{ row }">{{ formatTime(row.purchaseTime) }}</template>
        </el-table-column>
        <el-table-column label="激活时间" min-width="158">
          <template slot-scope="{ row }">{{ row.activateTime ? formatTime(row.activateTime) : '—' }}</template>
        </el-table-column>
        <el-table-column label="有效期至" width="120" align="center">
          <template slot-scope="{ row }">
            <span :class="{ 'expire-warn': isExpireWarn(row) }">{{ formatDate(row.expireDate) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="所属终端编号" min-width="140">
          <template slot-scope="{ row }">
            <span :class="{ 'text-muted': !row.terminalCode }">{{ row.terminalCode || '未绑定' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="projectName" label="所属项目" min-width="140" show-overflow-tooltip />
        <el-table-column label="所属组织" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ orgName(row.orgId) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" class="op-edit" icon="el-icon-edit" @click="openEdit(row, emptyForm())">编辑</el-button>
            <el-button type="text" size="small" class="op-detail" icon="el-icon-view" @click="openDetail(row)">详情</el-button>
            <el-button type="text" size="small" class="op-del" icon="el-icon-delete" @click="removeRow(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :current-page="query.pageNum"
        :page-size="query.pageSize"
        :page-sizes="[10, 20, 50]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="680px">
      <el-form :model="form" label-width="100px" size="small">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="运营商" required>
              <el-select v-model="form.operatorName" placeholder="请选择" style="width:100%">
                <el-option v-for="o in operatorOptions" :key="o" :label="o" :value="o" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="卡类型" required>
              <el-select v-model="form.cardType" placeholder="请选择" style="width:100%">
                <el-option v-for="t in cardTypeOptions" :key="t" :label="t" :value="t" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item label="ICCID" required><el-input v-model="form.iccid" :disabled="!!form.cardId" /></el-form-item></el-col>
          <el-col :span="12">
            <el-form-item label="所属组织">
              <el-select v-model="form.orgId" clearable placeholder="请选择" style="width:100%">
                <el-option v-for="o in orgOptions" :key="o.orgId" :label="o.orgName" :value="o.orgId" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item label="所属项目"><el-input v-model="form.projectName" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="终端编号"><el-input v-model="form.terminalCode" placeholder="未绑定可留空" /></el-form-item></el-col>
          <el-col :span="12">
            <el-form-item label="卡状态">
              <el-select v-model="form.cardStatus" style="width:100%">
                <el-option label="已激活" value="active" />
                <el-option label="未激活" value="inactive" />
                <el-option label="已过期" value="expired" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="采购时间">
              <el-date-picker v-model="form.purchaseTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="激活时间">
              <el-date-picker v-model="form.activateTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="有效期至">
              <el-date-picker v-model="form.expireDate" type="date" value-format="yyyy-MM-dd" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="24"><el-form-item label="备注"><el-input v-model="form.remark" type="textarea" :rows="2" /></el-form-item></el-col>
        </el-row>
      </el-form>
      <span slot="footer"><el-button @click="dialogVisible = false">取消</el-button><el-button type="primary" @click="submitForm">保存</el-button></span>
    </el-dialog>

    <el-dialog title="物联网卡详情" :visible.sync="detailVisible" width="560px">
      <el-descriptions v-if="detailRow" :column="1" border size="small">
        <el-descriptions-item label="运营商">{{ detailRow.operatorName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="卡类型">{{ detailRow.cardType || '—' }}</el-descriptions-item>
        <el-descriptions-item label="ICCID">{{ detailRow.iccid || '—' }}</el-descriptions-item>
        <el-descriptions-item label="卡状态">
          <gas-status-tag kind="card" :value="detailRow.cardStatus" />
        </el-descriptions-item>
        <el-descriptions-item label="采购时间">{{ formatTime(detailRow.purchaseTime) }}</el-descriptions-item>
        <el-descriptions-item label="激活时间">{{ detailRow.activateTime ? formatTime(detailRow.activateTime) : '—' }}</el-descriptions-item>
        <el-descriptions-item label="有效期至">{{ formatDate(detailRow.expireDate) }}</el-descriptions-item>
        <el-descriptions-item label="所属终端编号">{{ detailRow.terminalCode || '未绑定' }}</el-descriptions-item>
        <el-descriptions-item label="所属项目">{{ detailRow.projectName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="所属组织">{{ orgName(detailRow.orgId) }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ detailRow.remark || '—' }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import GasStatusTag from '@/components/GasStatusTag.vue'
import gasCrudPage from '@/mixins/gasCrudPage'
import { gasApi } from '@/api/gas'

const emptyForm = () => ({ cardStatus: 'inactive' })

const OPERATORS = ['中国移动', '中国联通', '中国电信']
const CARD_TYPES = ['插拔卡', '贴片卡']

export default {
  components: { GasPageShell, GasStatusTag },
  mixins: [gasCrudPage],
  data() {
    return {
      loadOrgOptions: true,
      idField: 'cardId',
      listApi: gasApi.iotCard.list,
      saveApi: gasApi.iotCard.save,
      removeApi: gasApi.iotCard.remove,
      batchRemoveApi: gasApi.iotCard.batchRemove,
      batchRemoveIdsWrapper: 'iot',
      operatorOptions: OPERATORS,
      cardTypeOptions: CARD_TYPES,
      projectOptions: [],
      purchaseRange: null,
      statCards: [],
      detailVisible: false,
      detailRow: null,
      query: {
        pageNum: 1,
        pageSize: 10,
        keyword: '',
        orgId: null,
        operatorName: '',
        cardType: '',
        projectName: '',
        beginPurchaseDate: '',
        endPurchaseDate: ''
      }
    }
  },
  created() {
    this.loadStats()
    this.loadProjectOptions()
  },
  methods: {
    emptyForm,
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => {
        if (p[k] === '' || p[k] === null) delete p[k]
      })
      return p
    },
    async loadData() {
      if (!this.listApi) return
      this.loading = true
      try {
        const res = await this.listApi(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    async loadStats() {
      const res = await gasApi.iotCard.stats()
      const d = res.data || {}
      this.statCards = [
        { key: 'total', label: '物联网卡总数', value: d.total || 0, icon: 'el-icon-mobile-phone', bg: '#e8f3ff', color: '#1677ff' },
        { key: 'active', label: '已激活', value: d.active || 0, icon: 'el-icon-circle-check', bg: '#e8ffea', color: '#00b42a', valueColor: '#00b42a' },
        { key: 'inactive', label: '未激活', value: d.inactive || 0, icon: 'el-icon-minus', bg: '#f2f3f5', color: '#86909c' },
        { key: 'expired', label: '已过期', value: d.expired || 0, icon: 'el-icon-alarm-clock', bg: '#ffece8', color: '#f53f3f', valueColor: '#f53f3f' },
        { key: 'monthNew', label: '本月新增', value: d.monthNew || 0, icon: 'el-icon-plus', bg: '#e8ffea', color: '#00b42a', valueColor: '#00b42a' }
      ]
    },
    async loadProjectOptions() {
      try {
        const res = await gasApi.iotCard.projectOptions()
        this.projectOptions = res.data || []
      } catch (e) {
        this.projectOptions = []
      }
    },
    refreshAll() {
      this.loadStats()
      this.loadProjectOptions()
      this.loadData()
    },
    resetSearch() {
      this.purchaseRange = null
      this.query = {
        pageNum: 1,
        pageSize: this.query.pageSize,
        keyword: '',
        orgId: null,
        operatorName: '',
        cardType: '',
        projectName: '',
        beginPurchaseDate: '',
        endPurchaseDate: ''
      }
      this.loadData()
    },
    onPurchaseRangeChange(val) {
      if (val && val.length === 2) {
        this.query.beginPurchaseDate = val[0]
        this.query.endPurchaseDate = val[1]
      } else {
        this.query.beginPurchaseDate = ''
        this.query.endPurchaseDate = ''
      }
    },
    rowNo(index) {
      return (this.query.pageNum - 1) * this.query.pageSize + index + 1
    },
    operatorClass(name) {
      if (!name) return ''
      if (name.includes('移动')) return 'op-mobile'
      if (name.includes('联通')) return 'op-unicom'
      if (name.includes('电信')) return 'op-telecom'
      return 'op-default'
    },
    formatTime(v) {
      if (!v) return '—'
      return String(v).replace('T', ' ').slice(0, 19)
    },
    formatDate(v) {
      if (!v) return '—'
      return String(v).slice(0, 10)
    },
    isExpireWarn(row) {
      if (!row.expireDate) return false
      if (row.cardStatus === 'expired') return true
      const parts = String(row.expireDate).slice(0, 10).split('-')
      if (parts.length < 3) return false
      const exp = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return exp <= today
    },
    openDetail(row) {
      this.detailRow = { ...row }
      this.detailVisible = true
    },
    afterBatchRemove() {
      this.refreshAll()
    },
    async exportExcel() {
      const res = await gasApi.iotCard.list({ ...this.buildQuery(), pageNum: 1, pageSize: 10000 })
      const rows = (res.data || {}).records || []
      const header = ['序号', '运营商', '卡类型', '采购时间', '激活时间', '有效期至', '所属终端编号', '所属项目', '所属组织', 'ICCID', '卡状态']
      const csv = [header.join(',')]
      rows.forEach((r, i) => {
        const statusMap = { active: '已激活', inactive: '未激活', expired: '已过期' }
        csv.push([
          i + 1,
          r.operatorName,
          r.cardType,
          this.formatTime(r.purchaseTime),
          r.activateTime ? this.formatTime(r.activateTime) : '',
          this.formatDate(r.expireDate),
          r.terminalCode || '未绑定',
          r.projectName,
          this.orgName(r.orgId),
          r.iccid,
          statusMap[r.cardStatus] || r.cardStatus
        ].map(v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(','))
      })
      const blob = new Blob(['\ufeff' + csv.join('\n')], { type: 'application/vnd.ms-excel;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '物联网卡列表.xls'
      link.click()
      URL.revokeObjectURL(link.href)
      this.$message.success('导出成功')
    },
    toggleFullscreen() {
      const el = this.$refs.tablePanel
      if (!el) return
      if (!document.fullscreenElement) {
        el.requestFullscreen && el.requestFullscreen()
      } else {
        document.exitFullscreen && document.exitFullscreen()
      }
    },
    async submitForm() {
      if (!this.form.iccid) return this.$message.warning('请填写 ICCID')
      if (!this.form.operatorName || !this.form.cardType) return this.$message.warning('请选择运营商和卡类型')
      await gasCrudPage.methods.submitForm.call(this)
      this.refreshAll()
    },
    async removeRow(row) {
      await gasCrudPage.methods.removeRow.call(this, row)
      this.refreshAll()
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/report-flow.scss';

.stat-row {
  grid-template-columns: repeat(5, 1fr);
}
@media (max-width: 1200px) {
  .stat-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

.search-section {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  padding: 16px 16px 4px;
  margin-bottom: 16px;
}
.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 12px;
}
.title-bar {
  width: 3px;
  height: 14px;
  background: #1677ff;
  margin-right: 8px;
}
.search-form >>> .el-form-item {
  margin-bottom: 12px;
}

.table-panel {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  padding: 16px;
}
.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.btn-del {
  color: #f53f3f;
  border-color: #f53f3f;
}
.btn-export {
  color: #00b42a;
  border-color: #00b42a;
}

.operator-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}
.op-mobile {
  background: #e8f3ff;
  color: #1677ff;
}
.op-unicom {
  background: #fff7e8;
  color: #ff7d00;
}
.op-telecom {
  background: #e8ffea;
  color: #00b42a;
}
.op-default {
  background: #f2f3f5;
  color: #4e5969;
}

.expire-warn {
  color: #f53f3f;
  font-weight: 500;
}
.text-muted {
  color: #86909c;
}

.op-edit {
  color: #1677ff;
}
.op-detail {
  color: #722ed1;
}
.op-del {
  color: #f53f3f;
}
</style>
